import { createContext, useState, useMemo, useCallback, useContext } from 'react'
import PropTypes from 'prop-types'
import { contextMenu } from 'react-contexify'
import { useSelector } from 'react-redux'
import { ApprovalContext } from '@templates/approval/contexts'
import { ApprovalContextMenu } from '../ApprovalContextMenu'
import { ContextMenu } from '../ContextMenu'
import { CellHistoryPopover } from '../cell-history/CellHistoryPopover'
import { getDataForSelection } from '../utils'

export const ReadGridContextMenuContext = createContext({
  openContextMenu: _obj => {},
})

export const ReadGridContextMenuProvider = ({
  data = [],
  enabled = false,
  selectiveEditModeEnabled = false,
  children,
}) => {
  const approvalContext = useContext(ApprovalContext)
  const [column, setColumn] = useState(null)
  const [cellData, setCellData] = useState(null)
  const [selectedCells, setSelectedCells] = useState(null)
  const [columns, setColumns] = useState(null)
  const [rowData, setRowData] = useState(null)
  const [grid, setGrid] = useState(null)
  const [cellRef, setCellRef] = useState(null)
  const [cellHistoryAnchorEl, setCellHistoryAnchorEl] = useState(null)

  const cellHistoryEnabled = useSelector(state => state.Auth.features['dataset-versions-ui'])
  const cellHistoryOpen = Boolean(cellHistoryAnchorEl)

  const openContextMenu = useCallback(
    ({
      event,
      column: _column,
      cellData: _cellData,
      grid: _grid,
      cellRef: _cellRef,
      rowData: _rowData,
      selectedCells: _selectedCells,
      columns: _columns,
    }) => {
      if (!enabled) {
        return
      }
      event.preventDefault()
      setColumn(_column)
      setCellData(_cellData)
      setGrid(_grid)
      setCellRef(_cellRef)
      setRowData(_rowData)
      setSelectedCells(_selectedCells)
      setColumns(_columns)
      contextMenu.show({ id: 'contextMenu', event })
    },
    [contextMenu, enabled],
  )

  const handleCellHistoryOpen = useCallback(() => {
    setCellHistoryAnchorEl(cellRef.current)
  }, [cellRef])

  const handleCellHistoryClose = useCallback(() => {
    setCellHistoryAnchorEl(null)
  }, [])

  const getDataForSelectionCallback = useCallback(
    selection =>
      getDataForSelection({ data, selection, columns, multiSelect: selectiveEditModeEnabled }),
    [data, columns],
  )

  const value = useMemo(
    () => ({
      openContextMenu,
      selectedCells,
      getDataForSelectionCallback,
    }),
    [openContextMenu, selectedCells, getDataForSelectionCallback],
  )

  const showApprovalContextMenu = approvalContext !== null

  return (
    <ReadGridContextMenuContext.Provider value={value}>
      <>
        {enabled && (
          <>
            {showApprovalContextMenu ? (
              <ApprovalContextMenu
                menuId="contextMenu"
                data={data}
                cellData={cellData}
                column={column}
              />
            ) : (
              <>
                <ContextMenu
                  menuId="contextMenu"
                  column={column}
                  cellData={cellData}
                  rowData={rowData}
                  onShowCellHistory={handleCellHistoryOpen}
                  readGridParent={grid}
                />

                {cellHistoryEnabled && (
                  <CellHistoryPopover
                    column={column}
                    cellData={cellData}
                    rowData={rowData}
                    open={cellHistoryOpen}
                    anchorEl={cellHistoryAnchorEl}
                    handlePopoverClose={handleCellHistoryClose}
                  />
                )}
              </>
            )}
          </>
        )}
        {children}
      </>
    </ReadGridContextMenuContext.Provider>
  )
}

ReadGridContextMenuProvider.propTypes = {
  data: PropTypes.array,
  enabled: PropTypes.bool,
  children: PropTypes.node.isRequired,
}
